import type { FC, HTMLAttributes, ReactNode } from 'react';
import { memo, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { apiUrl } from '../../config';
import { usePermissions } from '../../hooks/usePermissions';
import { useWebsite } from '../../hooks/useWebsite';
import { commonMessages } from '../../i18n/common';
import axios from '../../services/axios';
import { GraphModal } from '../GraphModal/GraphModal';
import type { ModalInstance } from '../GraphModal/Modal';

const messages = defineMessages({
  confirmDeleteTitle: {
    defaultMessage: 'Are you sure you want to delete your profile on this site?',
    id: 'hY565h',
  },
  confirmDeleteBody: {
    defaultMessage: `<p>Warning: Deleting this profile from this account will delete all personal data associated with this profile, as well as:</p>
<p>- all your comments and topics posted on this site</p>`,
    id: 'UOEERh',
  },
});

type ProfileDeletionModaleProps = HTMLAttributes<HTMLDivElement> & {
  poper: ReactNode;
  onShowImmediate?: () => void | Promise<void>;
};

export const ProfileDeletionModale: FC<ProfileDeletionModaleProps> = memo(
  function ProfileDeletionModale({ poper, className, onShowImmediate, ...otherProps }) {
    const { id: websiteId } = useWebsite();
    const { logout } = usePermissions();

    const handleConfirmDeleteProfile = useCallback(async () => {
      try {
        await axios.delete(`${apiUrl}/api/users/deleteProfile/${websiteId}`);
        await logout();
        toast.success(<FormattedMessage {...commonMessages.deletedSuccessfully} />);
      } catch (error) {
        console.error('Error deleting profile:', error);
      }
    }, [logout, websiteId]);

    const lazyRender = useCallback(
      (modal: ModalInstance) => (
        <div className="text-sm">
          <h2 className="mb-4 text-lg font-semibold text-[#f44336]">
            <FormattedMessage {...messages.confirmDeleteTitle} />
          </h2>
          <div className="modal-body mb-6">
            <FormattedMessage
              {...messages.confirmDeleteBody}
              values={{
                p: (chunks: React.ReactNode) => <p className="mb-2">{chunks}</p>,
              }}
              tagName={'div'}
            />
            <FormattedMessage {...commonMessages.areYouSure} />
          </div>
          <div className="flex justify-end gap-6">
            <button type="button" className="text-sm text-gray-500" onClick={modal.hide}>
              <FormattedMessage {...commonMessages.no} />
            </button>
            <button
              type="button"
              className="px-4 py-1 text-sm text-white bg-primary rounded"
              onClick={async () => {
                await handleConfirmDeleteProfile();
                modal.hide();
              }}
            >
              <FormattedMessage {...commonMessages.yes} />
            </button>
          </div>
        </div>
      ),
      [handleConfirmDeleteProfile],
    );

    return (
      <div className={twMerge('ProfileDeletionModale', className)} {...otherProps}>
        <GraphModal
          lazyContent={lazyRender}
          onShowImmediate={onShowImmediate}
          showPageOverlay
          forceModal
        >
          {poper}
        </GraphModal>
      </div>
    );
  },
);
